import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './JourneyDetail.css';
import Menu from '../Shared/Menu';
import usePageTitle from '../Shared/usePageTitle';

const API_URL        = '/rest/sfmc/journeys';
const EMAILS_URL     = '/rest/sfmc/journeys/emails';
const DES_URL        = '/rest/sfmc/journeys/data-extensions';
const AUTOS_URL      = '/rest/sfmc/journeys/automations';

/* ─── Helpers ─── */

function formatNumber(num) {
    if (num == null) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return String(num);
}

function formatDate(dateStr) {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function normalizeStatus(raw) {
    if (!raw) return 'Draft';
    const s = raw.toLowerCase();
    if (s === 'running' || s === 'active' || s.includes('active')) return 'Active';
    if (s === 'published') return 'Published';
    if (s === 'stopped' || s === 'paused' || s.includes('pause')) return 'Paused';
    if (s === 'draft') return 'Draft';
    return raw;
}

function statusBadge(status) {
    if (status === 'Active')    return { cls: 'status-badge-active', dot: 'status-dot-active' };
    if (status === 'Published') return { cls: 'status-badge-active', dot: 'status-dot-active' };
    if (status === 'Paused')    return { cls: 'status-badge-paused', dot: 'status-dot-paused' };
    return { cls: 'status-badge-draft', dot: 'status-dot-draft' };
}

function metricColor(value, thresholds) {
    if (value >= thresholds.good) return '#10B981';
    if (value >= thresholds.warn) return '#F59E0B';
    return '#EF4444';
}

/* ─── Goal Ring SVG ─── */
function GoalRing({ percent, color }) {
    const r = 27.5;
    const circumference = 2 * Math.PI * r;
    const filled = (percent / 100) * circumference;
    return (
        <div className="ring-container">
            <svg className="ring-svg" width="68" height="68">
                <circle cx="34" cy="34" r={r} fill="none" stroke="#F1F5F9" strokeWidth="7" />
                <circle cx="34" cy="34" r={r} fill="none" stroke={color} strokeWidth="7"
                    strokeDasharray={`${filled} ${circumference - filled}`}
                    strokeLinecap="round"
                    style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
                />
            </svg>
            <div className="ring-text" style={{ color }}>{percent}%</div>
        </div>
    );
}

/* ─── Email Metric Bar ─── */
function MetricBar({ label, value, percent, color }) {
    return (
        <div>
            <div className="d-flex justify-content-between align-items-baseline mb-2">
                <span className="email-detail-label">{label}</span>
                <div className="d-flex align-items-baseline gap-2">
                    <span className="email-detail-value">{formatNumber(value)}</span>
                    <span className="email-detail-percent">{percent}%</span>
                </div>
            </div>
            <div className="progress-bar-custom" style={{ height: 5 }}>
                <div className="progress-bar-fill" style={{ width: `${percent}%`, background: color }} />
            </div>
        </div>
    );
}

/* ─── Email Card ─── */
function EmailCard({ email }) {
    const sent       = email.sent || email.emailsSent || email.emailSent || email.numSent || 0;
    const delivered  = email.delivered || email.numDelivered || (sent > 0 ? Math.round(sent * 0.98) : 0);
    const opened     = email.opened || email.uniqueOpens || email.numOpens || 0;
    const clicked    = email.clicked || email.uniqueClicks || email.numClicks || 0;
    const bounced    = email.bounced || email.bounces || email.numBounces || 0;
    const unsub      = email.unsubscribed || email.unsubscribes || email.numUnsubscribes || 0;

    const delivPct   = sent > 0 ? ((delivered / sent) * 100).toFixed(1) : 0;
    const openPct    = delivered > 0 ? ((opened / delivered) * 100).toFixed(1) : 0;
    const ctorPct    = opened > 0 ? ((clicked / opened) * 100).toFixed(1) : 0;
    const bouncePct  = sent > 0 ? ((bounced / sent) * 100).toFixed(1) : 0;
    const unsubPct   = sent > 0 ? ((unsub / sent) * 100).toFixed(1) : 0;

    const openColor  = metricColor(parseFloat(openPct), { good: 40, warn: 25 });
    const ctorColor  = metricColor(parseFloat(ctorPct), { good: 40, warn: 25 });

    return (
        <div className="email-card mb-3">
            <div className="d-flex justify-content-between align-items-start mb-3">
                <div className="d-flex align-items-center gap-3">
                    <div className="email-icon-box">
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="1.8">
                            <rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 7L2 7" />
                        </svg>
                    </div>
                    <div>
                        <p className="email-name">{email.name || 'Unnamed Email'}</p>
                        <p className="email-sent">{formatNumber(sent)} sent</p>
                    </div>
                </div>
                <div className="d-flex gap-4">
                    <div className="text-end">
                        <p className="email-metric-label">Open Rate</p>
                        <p className="email-metric-value" style={{ color: openColor }}>{openPct}%</p>
                    </div>
                    <div style={{ width: 1, background: '#F1F5F9' }} />
                    <div className="text-end">
                        <p className="email-metric-label">CTOR</p>
                        <p className="email-metric-value" style={{ color: ctorColor }}>{ctorPct}%</p>
                    </div>
                </div>
            </div>

            <div className="d-flex flex-column gap-3">
                <MetricBar label="Delivered" value={delivered} percent={delivPct} color="#3B82F6" />
                <MetricBar label="Opened" value={opened} percent={openPct} color={openColor} />
                <MetricBar label="Clicked" value={clicked} percent={ctorPct} color={ctorColor} />
                <MetricBar label="Bounced" value={bounced} percent={bouncePct} color="#94A3B8" />
                <MetricBar label="Unsubscribed" value={unsub} percent={unsubPct} color="#EF4444" />
            </div>
        </div>
    );
}

/* ─── DE List Card ─── */
function DEListCard({ de }) {
    return (
        <div className="de-list-card mb-3">
            <div className="d-flex align-items-center gap-3">
                <div className="list-card-icon-box list-card-icon-de-sendable">
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="1.8">
                        <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5v14a9 3 0 0 0 18 0V5" /><path d="M3 12a9 3 0 0 0 18 0" />
                    </svg>
                </div>
                <div>
                    <p className="list-card-name">{de.name || 'Unnamed DE'}</p>
                    <p className="list-card-meta">
                        {de.lastUpdated ? `Updated ${formatDate(de.lastUpdated)}` : ''}
                        {de.fieldCount ? ` \u00b7 ${de.fieldCount} fields` : ''}
                    </p>
                </div>
            </div>
            <div className="d-flex align-items-center gap-3">
                {de.rowCount != null && (
                    <div className="text-end">
                        <p className="list-card-value">{formatNumber(de.rowCount)}</p>
                        <p className="list-card-value-label">rows</p>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ─── Automation List Card ─── */
function AutoListCard({ auto }) {
    return (
        <div className="auto-list-card mb-3">
            <div className="d-flex align-items-center gap-3">
                <div className="list-card-icon-box list-card-icon-auto">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="1.8">
                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                </div>
                <div>
                    <p className="list-card-name">{auto.name || 'Unnamed Automation'}</p>
                    <p className="list-card-meta">
                        {auto.lastRun ? `Last: ${formatDate(auto.lastRun)}` : ''}
                        {auto.activityCount ? ` \u00b7 ${auto.activityCount} activities` : ''}
                    </p>
                </div>
            </div>
            <div className="d-flex align-items-center gap-3">
                {auto.status && (
                    <span className="status-badge-scheduled">
                        <span className="status-dot-scheduled" />
                        {auto.status}
                    </span>
                )}
            </div>
        </div>
    );
}

/* ─── Main Detail Page ─── */
function JourneyDetail() {
    const { id }    = useParams();
    const navigate  = useNavigate();
    usePageTitle('Journey Detail');
    const [journey,    setJourney]    = useState(null);
    const [emails,     setEmails]     = useState([]);
    const [des,        setDes]        = useState([]);
    const [autos,      setAutos]      = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [error,      setError]      = useState(null);
    const [activeTab,  setActiveTab]  = useState('emails');

    useEffect(() => {
        // First fetch the journey list to find this journey
        axios.get(API_URL)
            .then(res => {
                const data = res.data;
                const list = Array.isArray(data) ? data : (data.journeys || data.items || data.data || []);
                const found = list.find(j => (j.id || j.journeyId) === id);
                if (!found) {
                    setError('Journey not found');
                    setLoading(false);
                    return;
                }
                setJourney(found);

                // Now fetch tabs data via POST with journeyId in body
                const body = { journeyId: id };
                return Promise.all([
                    axios.post(EMAILS_URL, body).catch(() => ({ data: {} })),
                    axios.post(DES_URL, body).catch(() => ({ data: {} })),
                    axios.post(AUTOS_URL, body).catch(() => ({ data: {} })),
                ]);
            })
            .then(results => {
                if (!results) return; // journey not found, already handled

                const [emailsRes, desRes, autosRes] = results;

                // Emails — response: { journeyName, count, items: [...] }
                const emailData = emailsRes.data;
                setEmails(Array.isArray(emailData) ? emailData : (emailData?.items || emailData?.emails || emailData?.data || []));

                // Data Extensions
                const desData = desRes.data;
                setDes(Array.isArray(desData) ? desData : (desData?.items || desData?.dataExtensions || desData?.data || []));

                // Automations
                const autosData = autosRes.data;
                setAutos(Array.isArray(autosData) ? autosData : (autosData?.items || autosData?.automations || autosData?.data || []));
            })
            .catch(err => {
                setError(err.response?.data?.message || err.message);
            })
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) return (
        <><Menu /><div className="container fade-up container-main"><div className="text-center py-5"><p>Loading journey...</p></div></div></>
    );
    if (error) return (
        <><Menu /><div className="container fade-up container-main"><div className="text-center py-5"><p className="text-danger">{error}</p></div></div></>
    );

    const status  = normalizeStatus(journey.status);
    const badge   = statusBadge(status);
    const version = journey.version ? `Version ${journey.version}` : '';
    const modified = journey.modifiedDate || journey.lastUpdated || journey.modifiedAt;
    const subtitle = [version, modified ? `Last modified ${formatDate(modified)}` : ''].filter(Boolean).join(' \u00b7 ');

    const entered    = journey.contactsEnteredCount || journey.entered || 0;
    const completed  = journey.completed  || journey.contactsCompleted || 0;
    const inProgress = entered - completed > 0 ? entered - completed : 0;
    const emailsSent = journey.emailSent  || journey.emailsSent || 0;
    const openRate   = journey.openRate   != null ? journey.openRate : null;
    const ctor       = journey.ctor       != null ? journey.ctor : null;
    const goalMet    = journey.goals      || journey.goalMet || 0;
    const completionRate = entered > 0 ? ((completed / entered) * 100).toFixed(1) : 0;

    const emailList      = emails.length > 0 ? emails : (journey.emails || journey.emailActivities || []);
    const dataExtensions = des.length > 0    ? des   : (journey.dataExtensions || journey.entrySourceDEs || []);
    const automationList = autos.length > 0  ? autos : (journey.automations || journey.relatedAutomations || []);

    const goalColor = goalMet >= 80 ? '#10B981' : goalMet >= 50 ? '#F59E0B' : '#EF4444';

    return (
        <>
            <Menu />

            <div className="container fade-up container-main">
                {/* Back Button */}
                <button className="back-button" onClick={() => navigate('/journeys')}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M19 12H5M12 5l-7 7 7 7" />
                    </svg>
                    All Journeys
                </button>

                {/* Header */}
                <div className="d-flex justify-content-between align-items-start mb-4 detail-header-row">
                    <div>
                        <h1 className="journey-detail-title">{journey.name}</h1>
                        {subtitle && <p className="journey-detail-subtitle">{subtitle}</p>}
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        <span className={badge.cls}>
                            <span className={badge.dot} />
                            {status}
                        </span>
                        <div className="d-flex align-items-center gap-2">
                            <GoalRing percent={goalMet} color={goalColor} />
                            <div>
                                <p className="ring-label">GOAL</p>
                                <p className="ring-sublabel">Completion</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* DE & Automation Pills */}
                {(dataExtensions.length > 0 || automationList.length > 0) && (
                    <div className="d-flex flex-wrap gap-2 mb-4">
                        {dataExtensions.map((de, i) => (
                            <span key={de.id || i} className="de-pill">
                                <svg className="pill-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5v14a9 3 0 0 0 18 0V5" /><path d="M3 12a9 3 0 0 0 18 0" />
                                </svg>
                                <span>{de.name || de.customerKey || de.dataExtensionName || (typeof de === 'string' ? de : de.id)}</span>
                                {de.rowCount != null && <span className="pill-count">{formatNumber(de.rowCount)}</span>}
                            </span>
                        ))}
                        {automationList.map((auto, i) => (
                            <span key={auto.id || i} className="auto-pill">
                                <span className="status-dot-pill" style={{ background: '#3B82F6' }} />
                                <svg className="pill-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                </svg>
                                <span>{auto.name || auto}</span>
                                {auto.stepCount != null && <span className="pill-count">{auto.stepCount} steps</span>}
                            </span>
                        ))}
                    </div>
                )}

                {/* KPI Tiles — 6-column grid matching HTML design */}
                <div className="journey-kpi-grid mb-4">
                    <div className="kpi-card kpi-card-detail">
                        <p className="journey-kpi-label">Entered</p>
                        <p className="journey-kpi-value" style={{ color: '#6366F1' }}>{formatNumber(entered)}</p>
                    </div>
                    <div className="kpi-card kpi-card-detail">
                        <p className="journey-kpi-label">Completed</p>
                        <p className="journey-kpi-value" style={{ color: '#10B981' }}>{formatNumber(completed)}</p>
                        {entered > 0 && <p className="journey-kpi-sub">{completionRate}%</p>}
                    </div>
                    <div className="kpi-card kpi-card-detail">
                        <p className="journey-kpi-label">In Progress</p>
                        <p className="journey-kpi-value" style={{ color: '#3B82F6' }}>{formatNumber(inProgress)}</p>
                    </div>
                    <div className="kpi-card kpi-card-detail">
                        <p className="journey-kpi-label">Emails Sent</p>
                        <p className="journey-kpi-value" style={{ color: '#8B5CF6' }}>{formatNumber(emailsSent)}</p>
                    </div>
                    <div className="kpi-card kpi-card-detail">
                        <p className="journey-kpi-label">Open Rate</p>
                        <p className="journey-kpi-value" style={{ color: '#F59E0B' }}>{openRate != null ? `${openRate}%` : 'N/A'}</p>
                    </div>
                    <div className="kpi-card kpi-card-detail">
                        <p className="journey-kpi-label">CTOR</p>
                        <p className="journey-kpi-value" style={{ color: '#EC4899' }}>{ctor != null ? `${ctor}%` : 'N/A'}</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="tab-buttons mb-4">
                    {['emails', 'data-extensions'].map(tab => (
                        <button
                            key={tab}
                            className={`tab-button${activeTab === tab ? ' active' : ''}`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab === 'emails' ? 'Emails' : 'Data Extensions'}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                {activeTab === 'emails' && (
                    <div>
                        {emailList.length > 0 ? (
                            emailList.map((email, i) => <EmailCard key={email.id || i} email={email} />)
                        ) : (
                            <div className="empty-tab">
                                <p>No email activity data available.</p>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'data-extensions' && (
                    <div>
                        {dataExtensions.length > 0 ? (
                            dataExtensions.map((de, i) => <DEListCard key={de.id || i} de={typeof de === 'string' ? { name: de } : de} />)
                        ) : (
                            <div className="empty-tab">
                                <p>No data extensions linked to this journey.</p>
                            </div>
                        )}
                    </div>
                )}

            </div>
        </>
    );
}

export default JourneyDetail;
